/*
 * Slide Templates — fetches the template manifest via DeckorateAPI's
 * blob-sas endpoint, same two-step SAS contract PhotoHeadshot's
 * photoHeadUpload.ts uses against the content backend, but read-only:
 *
 *   1. POST /v1/deckorate/content/blob-sas { client_id, transaction_type,
 *      template_type } -> a short-lived READ sas_url, scoped to the manifest
 *      blob's CONTAINING FOLDER (a directory SAS, `sr=d`), plus the
 *      manifest's own blob_path within that folder.
 *   2. Rebuild the manifest's actual file URL from that directory sas_url +
 *      blob_path (see this file's own directorySasFileUrl below — the token
 *      isn't bound to one filename), then GET it directly (Azure Blob
 *      Storage, not DeckorateAPI — the SAS query string is the only auth it
 *      needs, so this bypasses the Deckorate HttpClient/authorizedFetch and
 *      uses a bare fetch, same as shared/services/blobImageUpload.ts's
 *      putBlob — its sasVersionHeaders is reused below for the same reason).
 *
 * The manifest is a "signatures" catalog — confirmed against a real response
 * from standard-layouts.signatures.json:
 *
 *   { version, folder, generatedAt, entryCount, entries: [ {
 *     id ("standard-layouts/Bio_Layout.pptx#1"), pptxFile, slideIndex,
 *     slideName, category, layoutClass, promptDescription, signatureVec,
 *     thumbnailRef: { cacheFile, filePath, slideNumber }, ...
 *   } ] }
 *
 * It's an AI layout-matching index, not a preview-ready template list: there
 * is NO rendered thumbnail image URL on an entry itself (`thumbnailRef` is a
 * pointer, not a fetchable URL) and NO base64/pptx bytes — only which slide,
 * in which source .pptx, matched. `normaliseManifestEntry` still speculatively
 * checks several likely field names (thumbnail_url, thumbnail, preview_url,
 * image_url, *_base64) directly on an entry so a future manifest schema
 * change is picked up without further changes here, but that's not where real
 * thumbnails actually come from today.
 *
 * REAL THUMBNAILS COME FROM A SEPARATE `.cache` SIBLING
 * The `thumbnailRef.cacheFile` an entry points at names a real file sitting
 * next to the manifest — "standard-layouts.cache" alongside
 * "standard-layouts.signatures.json" — a flat JSON array of `{SlideNumber,
 * SlideName, FilePath, Category?, Base64ImageData}`, one real rendered
 * thumbnail per slide (same shape the Presentation Templates feature's own
 * aggregate `.cache` file uses — see presentation-templates/services/
 * presentationTemplatesApi.ts's module header — though these two features
 * still share no code). `fetchSlideTemplates` fetches it once, builds a
 * `${pptxFile basename}#${slideIndex}` lookup, and fills in each entry's
 * `thumbnailUrl` from it. The cache file is optional-best-effort: if it's
 * missing or fails to parse, entries just fall back to their wireframe
 * preview (below) same as before — never a hard failure for the whole tab.
 *
 * INSERTABILITY, WITHOUT A PAYLOAD FIELD
 * The manifest's directory SAS (see step 2 above) grants read access to every
 * sibling blob under the folder — including each entry's own source .pptx,
 * not just the manifest file. So each entry's `pptxFile` is resolved into a
 * fetchable `sourceFileUrl` at normalise time (same directorySasFileUrl swap,
 * just with the entry's filename instead of the manifest's). Insert time then
 * fetches that whole (possibly multi-slide) source file and carves out just
 * this entry's `slideIndex` — see services/slideTemplateInsert.ts, which
 * reuses CompareSlides/services/pptxSubdeck.ts's slide-list trim for that.
 *
 * WHAT THE CARDS SHOW INSTEAD
 * With no thumbnail image, 119 entries would all render as the same empty
 * placeholder box, so the entry's `placeholders[].bbox` + `slideDimensionsEmu`
 * are normalised into a `preview` — the placeholder rectangles as percentages
 * of the slide — and the panel draws that as a wireframe. It's the only
 * per-entry visual the manifest actually contains, and it makes each card
 * recognisably its own layout.
 *
 * `slideName` repeats for every slide in the same .pptx ("Bio_Layout" ×4), so
 * `displayName` combines it with the slide index rather than using it raw.
 *
 * `normaliseManifestEntry` still accepts a couple of alternate field-name
 * spellings (`name`/`label`/`title` alongside `slideName`) so a future schema
 * tweak degrades to "fewer templates shown" instead of a crash.
 */

import type { HttpClient, HttpRequestOptions } from "../../../services/deckorateApi";
import { directorySasFileUrl, sasVersionHeaders } from "../../shared/services/blobImageUpload";
import { getErrorMessage } from "../../shared/services/connectorErrors";
import {
  clampPct,
  roundPct,
  toPreviewRole,
  type SlidePreview,
  type SlidePreviewBlock,
} from "../../shared/services/slidePreviewGeometry";
import { logError, scrubMessage } from "../../../../../telemetry";

const SLIDE_TEMPLATES_CONNECTOR = "deckorate";
const BLOB_SAS_PATH = "/v1/deckorate/content/blob-sas";

/*
 * NOTE: client_id and template_type are hardcoded for now — this endpoint
 * doesn't yet distinguish tenants/template sets on this call path. Once the
 * real Studio client id (e.g. useOptionalAppConfig()?.data?.client.id) and a
 * selectable template type are available, replace these constants with the
 * real values instead of the literals below.
 */
const SLIDE_TEMPLATES_CLIENT_ID = 1;
const SLIDE_TEMPLATES_TRANSACTION_TYPE = "TemplateTransaction";
const SLIDE_TEMPLATES_TEMPLATE_TYPE = "Standard";

/**
 * Which registered library a mint targets. Each `template_type` resolves to
 * ONE folder of layouts (see the non-AI backend's 0002_SeedClientAssets.sql):
 *   "Standard" -> .../templates/slides/standard-layouts/
 *   "Template" -> .../templates/slides/transform-layouts/
 * This tab only ever shows "Standard"; the Create-slides flow needs both,
 * because the outline ranker scores over every folder in the backend's
 * CATALOG_BLOB_NAMES.
 */
export type SlideTemplateLibraryType = "Standard" | "Template";

const MANIFEST_REQUEST_FAILED_MESSAGE =
  "Couldn't find the slide template library — the blob-sas request didn't return a usable asset.";
const MANIFEST_DOWNLOAD_FAILED_MESSAGE = "Downloading the slide template library failed.";
const MANIFEST_NOT_JSON_MESSAGE = "The slide template library response wasn't valid JSON.";

export function logSlideTemplatesError(reason: string, message: string): void {
  logError(
    "error.connector.failure",
    { type: "SlideTemplatesError", scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: SLIDE_TEMPLATES_CONNECTOR, reason },
  );
}

interface BlobSasRequest {
  client_id: number;
  transaction_type: typeof SLIDE_TEMPLATES_TRANSACTION_TYPE;
  template_type: SlideTemplateLibraryType;
}

interface RawBlobSasAsset {
  sas_url?: unknown;
  blob_container?: unknown;
  blob_path?: unknown;
}

interface RawBlobSasResponse {
  assets?: unknown;
}

interface ManifestSasAsset {
  readonly sasUrl: string;
  readonly blobPath: string;
}

function firstManifestSasAsset(response: RawBlobSasResponse | null | undefined): ManifestSasAsset | null {
  const assets = response?.assets;
  if (!Array.isArray(assets) || assets.length === 0) return null;
  const asset = assets[0] as RawBlobSasAsset | undefined;
  if (typeof asset?.sas_url !== "string" || !asset.sas_url) return null;
  if (typeof asset.blob_path !== "string" || !asset.blob_path) return null;
  return { sasUrl: asset.sas_url, blobPath: asset.blob_path };
}

export type { SlidePreviewRole } from "../../shared/services/slidePreviewGeometry";
export type { SlidePreviewBlock, SlidePreview };

export interface SlideTemplate {
  readonly id: string;
  readonly name: string;
  readonly category: string | null;
  /** Layout family the indexer assigned (e.g. "list", "comparison"). */
  readonly layoutClass: string | null;
  /** The indexer's prose summary — used as the card's tooltip. */
  readonly description: string | null;
  readonly thumbnailUrl: string | null;
  /** Placeholder wireframe, or null when the entry carries no usable geometry. */
  readonly preview: SlidePreview | null;
  /** Whether this entry has enough information (`sourceFileUrl` + `slideIndex`)
   *  to be fetched and inserted. Tracked separately from those two fields
   *  because the Office Dialog window receives a copy of the manifest with
   *  both stripped (see asset-library/services/assetLibraryDialogChannel.ts)
   *  yet still has to mark which cards are insertable — so the UI must never infer
   *  insertability from `sourceFileUrl`/`slideIndex` being non-null. */
  readonly insertable: boolean;
  /** Directory-SAS URL for this entry's own source `.pptx` blob (a sibling of
   *  the manifest file — see the module header) — null when the entry carries
   *  no `pptxFile`, or throughout the dialog's stripped copy. Multi-slide: the
   *  file this points at is the WHOLE source deck, not just this entry's
   *  slide; `slideIndex` says which one to carve out at insert time. */
  readonly sourceFileUrl: string | null;
  /** 1-based position of this entry's slide within the file at `sourceFileUrl`. */
  readonly slideIndex: number | null;
}

function firstString(...candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate;
  }
  return null;
}

function firstNumber(...candidates: unknown[]): number | null {
  for (const candidate of candidates) {
    if (typeof candidate === "number" && Number.isFinite(candidate)) return candidate;
  }
  return null;
}

/** 16:9 — what every deck in this library uses; only a guard against bad dims. */
const DEFAULT_SLIDE_ASPECT = 16 / 9;
const MIN_SLIDE_ASPECT = 0.5;
const MAX_SLIDE_ASPECT = 4;

/**
 * Cap on wireframe rectangles per card. Entries go up to ~30 placeholders and
 * past a couple of dozen the preview is visual noise at thumbnail size — and
 * the whole manifest has to fit in localStorage for the dialog hand-off.
 */
const MAX_PREVIEW_BLOCKS = 24;
/** Rectangles smaller than this (percent of a side) don't survive downscaling. */
const MIN_BLOCK_SIZE_PCT = 1;

/**
 * One placeholder → a clamped percentage rect. Returns null for anything that
 * wouldn't draw: missing/non-finite geometry, zero area, fully off-slide
 * (some entries carry bleed images at x=1588, y=-1318), or sub-pixel slivers.
 */
function toPreviewBlock(raw: unknown, slideCx: number, slideCy: number): SlidePreviewBlock | null {
  if (typeof raw !== "object" || raw === null) return null;
  const placeholder = raw as Record<string, unknown>;
  const bbox = placeholder.bbox;
  if (typeof bbox !== "object" || bbox === null) return null;
  const rect = bbox as Record<string, unknown>;

  const x = firstNumber(rect.x);
  const y = firstNumber(rect.y);
  const cx = firstNumber(rect.cx);
  const cy = firstNumber(rect.cy);
  if (x === null || y === null || cx === null || cy === null) return null;
  if (cx <= 0 || cy <= 0) return null;

  const left = clampPct((x / slideCx) * 100);
  const top = clampPct((y / slideCy) * 100);
  const right = clampPct(((x + cx) / slideCx) * 100);
  const bottom = clampPct(((y + cy) / slideCy) * 100);
  const w = right - left;
  const h = bottom - top;
  if (w < MIN_BLOCK_SIZE_PCT || h < MIN_BLOCK_SIZE_PCT) return null;

  return { role: toPreviewRole(placeholder.role), x: roundPct(left), y: roundPct(top), w: roundPct(w), h: roundPct(h) };
}

/**
 * Placeholder geometry → wireframe. Grouped shapes make the manifest repeat
 * byte-identical rects (Bio_Layout#2 lists the same "Text Placeholder 82"
 * three times), so identical rects collapse into one; drawing them stacked
 * would only darken the same box. Largest-first, so a full-bleed background
 * image can't be painted over the text blocks that sit on top of it.
 */
function toPreview(raw: unknown, dimensions: unknown): SlidePreview | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;

  const dims = (typeof dimensions === "object" && dimensions !== null ? dimensions : {}) as Record<string, unknown>;
  const slideCx = firstNumber(dims.cx);
  const slideCy = firstNumber(dims.cy);
  if (slideCx === null || slideCy === null || slideCx <= 0 || slideCy <= 0) return null;

  const rawAspect = slideCx / slideCy;
  const aspect =
    rawAspect >= MIN_SLIDE_ASPECT && rawAspect <= MAX_SLIDE_ASPECT
      ? Math.round(rawAspect * 1000) / 1000
      : DEFAULT_SLIDE_ASPECT;

  const seen = new Set<string>();
  const blocks: SlidePreviewBlock[] = [];
  for (const placeholder of raw) {
    const block = toPreviewBlock(placeholder, slideCx, slideCy);
    if (!block) continue;
    const key = `${block.role}:${block.x}:${block.y}:${block.w}:${block.h}`;
    if (seen.has(key)) continue;
    seen.add(key);
    blocks.push(block);
  }
  if (blocks.length === 0) return null;

  blocks.sort((a, b) => b.w * b.h - a.w * a.h);
  return { aspect, blocks: blocks.slice(0, MAX_PREVIEW_BLOCKS) };
}

/**
 * Re-validates a preview that came back from storage rather than the manifest.
 * The dialog hand-off round-trips through localStorage, which can outlive the
 * build that wrote it, so the stored shape is treated as untrusted input —
 * same discipline as the manifest itself. Bad blocks are dropped, not fixed.
 */
export function toValidPreview(raw: unknown): SlidePreview | null {
  if (typeof raw !== "object" || raw === null) return null;
  const preview = raw as Record<string, unknown>;

  const rawAspect = firstNumber(preview.aspect);
  const aspect =
    rawAspect !== null && rawAspect >= MIN_SLIDE_ASPECT && rawAspect <= MAX_SLIDE_ASPECT
      ? rawAspect
      : DEFAULT_SLIDE_ASPECT;

  if (!Array.isArray(preview.blocks)) return null;
  const blocks: SlidePreviewBlock[] = [];
  for (const rawBlock of preview.blocks.slice(0, MAX_PREVIEW_BLOCKS)) {
    if (typeof rawBlock !== "object" || rawBlock === null) continue;
    const block = rawBlock as Record<string, unknown>;
    const rawX = firstNumber(block.x);
    const rawY = firstNumber(block.y);
    const rawW = firstNumber(block.w);
    const rawH = firstNumber(block.h);
    if (rawX === null || rawY === null || rawW === null || rawH === null) continue;

    const x = clampPct(rawX);
    const y = clampPct(rawY);
    const w = Math.min(rawW, 100 - x);
    const h = Math.min(rawH, 100 - y);
    if (w < MIN_BLOCK_SIZE_PCT || h < MIN_BLOCK_SIZE_PCT) continue;

    blocks.push({
      role: toPreviewRole(block.role),
      x: roundPct(x),
      y: roundPct(y),
      w: roundPct(w),
      h: roundPct(h),
    });
  }
  return blocks.length > 0 ? { aspect, blocks } : null;
}

/** Handles both `/`-separated blob paths and `\`-separated Windows-style
 *  paths a `.cache` file's own `FilePath` field may use. */
function basename(path: string): string {
  const segments = path.split(/[/\\]/);
  for (let i = segments.length - 1; i >= 0; i--) {
    if (segments[i]) return segments[i]!;
  }
  return path;
}

/** Sniffs an image format from its base64 payload's leading bytes — the
 *  `.cache` file carries no explicit mime/extension field alongside
 *  `Base64ImageData`. Every sample seen so far is PNG (`iVBORw0KGgo...`, the
 *  standard PNG signature base64-encoded), kept as the fallback for a format
 *  this doesn't recognise rather than dropping the image outright. */
function sniffImageMimeType(base64: string): string {
  if (base64.startsWith("/9j/")) return "image/jpeg";
  if (base64.startsWith("R0lGOD")) return "image/gif";
  if (base64.startsWith("Qk")) return "image/bmp";
  return "image/png";
}

/** `${pptxFile basename, lowercased}#${slideIndex}` -> a ready `data:` URL.
 *  Both sides of every lookup go through this so casing/path-separator
 *  differences between the manifest's `pptxFile` and the cache's `FilePath`
 *  never cause a miss. */
function thumbnailLookupKey(pptxFile: string, slideIndex: number): string {
  return `${basename(pptxFile).toLowerCase()}#${slideIndex}`;
}

/**
 * The same key, derived from a catalog id ("transform-layouts/Cover_Slide.pptx#2")
 * instead of a manifest/cache entry's separate filename+index fields. A
 * ranked candidate straight off the OUTLINE response only ever carries an id
 * like this — no manifest entry to read a filename+index off of — so this is
 * what lets one be recognised as "wanted" against streamed `.cache` entries
 * (see fetchSlideTemplateThumbnailsStreaming). Returns null for an id with no
 * "#N" suffix, which the outline's own ids always have.
 */
export function thumbnailKeyFromCatalogId(id: string): string | null {
  const match = /^(.*)#(\d+)\s*$/.exec(id);
  if (!match) return null;
  return thumbnailLookupKey(match[1]!, Number.parseInt(match[2]!, 10));
}

interface RawThumbnailCacheEntry {
  FilePath?: unknown;
  SlideNumber?: unknown;
  Base64ImageData?: unknown;
}

/**
 * Parses the `.cache` sibling into a lookup map. Never throws — a malformed
 * or unexpectedly-shaped file just yields an empty map, so every card falls
 * back to its wireframe preview rather than the whole tab failing.
 */
function buildThumbnailLookup(raw: unknown): ReadonlyMap<string, string> {
  const lookup = new Map<string, string>();
  if (!Array.isArray(raw)) return lookup;

  for (const item of raw) {
    if (typeof item !== "object" || item === null) continue;
    const entry = item as RawThumbnailCacheEntry;

    const filePath = firstString(entry.FilePath);
    const slideNumber = firstNumber(entry.SlideNumber);
    const imageBase64 = firstString(entry.Base64ImageData);
    if (!filePath || slideNumber === null || !imageBase64) continue;

    const key = thumbnailLookupKey(filePath, slideNumber);
    if (!lookup.has(key)) lookup.set(key, `data:${sniffImageMimeType(imageBase64)};base64,${imageBase64}`);
  }
  return lookup;
}

/** "Bio_Layout.pptx" / "Bio_Layout" → "Bio Layout". */
function humaniseSlideLabel(raw: string): string {
  return raw
    .replace(/\.pptx$/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * A card's display label. `slideName` is per-FILE, so entries for different
 * slides of the same source .pptx can share one (e.g. four Bio_Layout
 * slides) — cards are labelled alike in that case rather than disambiguated
 * with a slide number, matching the design's plain "Cover_Hero" / "5_Column"
 * style names. `id` (see normaliseManifest's uniqueId) is what selection and
 * insert actually key off, so a shared display name is cosmetic only. An
 * explicitly-named entry (a future schema) is trusted as-is.
 */
function toDisplayName(entry: Record<string, unknown>): string | null {
  const explicit = firstString(entry.name, entry.label, entry.title);
  if (explicit) return explicit;

  const base = firstString(entry.slideName, entry.pptxFile);
  if (base) {
    const humanised = humaniseSlideLabel(base);
    if (humanised) return humanised;
  }
  return firstString(entry.promptDescription);
}

function normaliseManifestEntry(
  raw: unknown,
  index: number,
  resolveSourceFileUrl: (pptxFile: string) => string,
  thumbnailLookup: ReadonlyMap<string, string>,
): SlideTemplate | null {
  if (typeof raw !== "object" || raw === null) return null;
  const entry = raw as Record<string, unknown>;

  const slideIndex = firstNumber(entry.slideIndex);
  const name = toDisplayName(entry);
  if (!name) return null;

  const id = firstString(entry.id, entry.template_id, entry.slug) ?? `slide-template-${index}`;
  const category = firstString(entry.category, entry.tag, entry.group);
  const layoutClass = firstString(entry.layoutClass, entry.layout_class);
  const description = firstString(entry.promptDescription, entry.prompt_description, entry.description);
  const preview = toPreview(entry.placeholders, entry.slideDimensionsEmu ?? entry.slide_dimensions_emu);
  const pptxFile = firstString(entry.pptxFile, entry.pptx_file);
  // The manifest entry itself carries no fetchable thumbnail field — see the
  // module comment above for where this really comes from. The speculative
  // direct-field lookups stay as a fallback for a future manifest schema.
  const thumbnailUrl =
    (pptxFile && slideIndex !== null ? thumbnailLookup.get(thumbnailLookupKey(pptxFile, slideIndex)) : undefined) ??
    firstString(
      entry.thumbnail_url,
      entry.thumbnail,
      entry.preview_url,
      entry.image_url,
      entry.thumbnail_base64,
      entry.preview_base64,
    );
  const sourceFileUrl = pptxFile && slideIndex !== null ? resolveSourceFileUrl(pptxFile) : null;

  return {
    id,
    name,
    category,
    layoutClass,
    description,
    thumbnailUrl,
    preview,
    insertable: sourceFileUrl !== null,
    sourceFileUrl,
    slideIndex,
  };
}

function normaliseManifest(
  raw: unknown,
  resolveSourceFileUrl: (pptxFile: string) => string,
  thumbnailLookup: ReadonlyMap<string, string>,
): SlideTemplate[] {
  let items: unknown[] | null = null;
  if (Array.isArray(raw)) {
    items = raw;
  } else if (Array.isArray((raw as { entries?: unknown })?.entries)) {
    items = (raw as { entries: unknown[] }).entries;
  } else if (Array.isArray((raw as { templates?: unknown })?.templates)) {
    items = (raw as { templates: unknown[] }).templates;
  }
  if (!items) {
    logSlideTemplatesError(
      "slide-templates-manifest-shape",
      "Manifest root was neither an array nor { entries: [] } / { templates: [] }",
    );
    return [];
  }
  // Ids are the selection key AND the only thing the Office Dialog posts back
  // for insertion, so a manifest that repeats one would make a card ambiguous.
  // Suffix duplicates rather than dropping them — the entries are still real
  // templates, they just need distinguishable keys.
  const seenIds = new Set<string>();
  const uniqueId = (id: string): string => {
    if (!seenIds.has(id)) {
      seenIds.add(id);
      return id;
    }
    let suffix = 2;
    while (seenIds.has(`${id}-${suffix}`)) suffix += 1;
    const deduped = `${id}-${suffix}`;
    seenIds.add(deduped);
    logSlideTemplatesError("slide-templates-manifest-duplicate-id", `Manifest repeated template id "${id}"`);
    return deduped;
  };

  return items
    .map((item, index) => normaliseManifestEntry(item, index, resolveSourceFileUrl, thumbnailLookup))
    .filter((item): item is SlideTemplate => item !== null)
    .map((item) => ({ ...item, id: uniqueId(item.id) }));
}

interface ManifestSasAssets {
  /** The manifest's directory-scoped SAS URL, unmodified — reusable for any
   *  sibling blob in the same folder (see directorySasFileUrl). */
  readonly directorySasUrl: string;
  /** The manifest's own container-relative path, e.g.
   *  ".../standard-layouts/standard-layouts.signatures.json" — used both to
   *  rebuild the manifest's own fetchable URL (directorySasFileUrl) and to
   *  derive the `.cache` sibling's filename (`deriveThumbnailCacheFileName`). */
  readonly manifestBlobPath: string;
}

async function requestManifestSasAssets(
  http: HttpClient,
  options: HttpRequestOptions,
  templateType: SlideTemplateLibraryType,
): Promise<ManifestSasAssets> {
  const request: BlobSasRequest = {
    client_id: SLIDE_TEMPLATES_CLIENT_ID,
    transaction_type: SLIDE_TEMPLATES_TRANSACTION_TYPE,
    template_type: templateType,
  };
  const response = await http.post<RawBlobSasResponse | null>(BLOB_SAS_PATH, request, options);
  const asset = firstManifestSasAsset(response);
  if (!asset) throw new Error(MANIFEST_REQUEST_FAILED_MESSAGE);
  return { directorySasUrl: asset.sasUrl, manifestBlobPath: asset.blobPath };
}

/**
 * "standard-layouts.signatures.json" -> "standard-layouts.cache" — the real-
 * thumbnail `.cache` sibling always sits next to the manifest, named after
 * the same base name with ".signatures.json" swapped for ".cache". Returns
 * null for a manifest filename that doesn't match that pattern (nothing to
 * derive from), which just means no cache-based thumbnails this fetch.
 */
function deriveThumbnailCacheFileName(manifestBlobPath: string): string | null {
  const name = basename(manifestBlobPath);
  if (!/\.signatures\.json$/i.test(name)) return null;
  return name.replace(/\.signatures\.json$/i, ".cache");
}

/**
 * Fetches and parses the `.cache` sibling into a thumbnail lookup —
 * best-effort: any failure (missing file, bad JSON) logs and resolves to an
 * empty map rather than rejecting, since this is a visual enhancement, not
 * something the tab's core function depends on.
 */
async function fetchThumbnailLookup(
  directorySasUrl: string,
  manifestBlobPath: string,
  signal: AbortSignal | undefined,
): Promise<ReadonlyMap<string, string>> {
  const cacheFileName = deriveThumbnailCacheFileName(manifestBlobPath);
  if (!cacheFileName) {
    // Silent otherwise, this is indistinguishable from "still fetching" to
    // anyone watching the UI — logged so "thumbnails never showed up" can be
    // told apart from "there was never a cache file to begin with" without
    // guessing from Network-tab traffic that never happens.
    console.info(`[slide-templates] no derivable thumbnail cache for ${manifestBlobPath} — skipping (wireframes only)`);
    return new Map();
  }

  const cacheFileUrl = directorySasFileUrl(directorySasUrl, cacheFileName, (message) =>
    logSlideTemplatesError("slide-templates-thumbnail-cache-url-rebuild", message),
  );
  const startedAt = Date.now();
  console.info(`[slide-templates] fetching thumbnail cache: ${cacheFileUrl}`);
  try {
    const res = await fetch(cacheFileUrl, {
      method: "GET",
      headers: sasVersionHeaders(cacheFileUrl, logSlideTemplatesError, "slide-templates-sas-version-parse"),
      ...(signal ? { signal } : {}),
    });
    if (!res.ok) throw new Error(`Thumbnail cache download failed (${res.status})`);
    const text = await res.text();
    const lookup = buildThumbnailLookup(JSON.parse(text));
    console.info(
      `[slide-templates] thumbnail cache ${cacheFileName}: ${lookup.size} photo(s) from ${text.length.toLocaleString()} chars in ${Date.now() - startedAt}ms`,
    );
    return lookup;
  } catch (error) {
    console.warn(`[slide-templates] thumbnail cache ${cacheFileName} failed after ${Date.now() - startedAt}ms:`, error);
    logSlideTemplatesError(
      "slide-templates-thumbnail-cache-fetch",
      getErrorMessage(error, "Thumbnail cache fetch/parse failed"),
    );
    return new Map();
  }
}

/**
 * Incrementally finds top-level `{...}` object substrings across a stream of
 * text chunks, calling `onObject` with each one's raw text the instant its
 * closing brace arrives — before the rest of the stream has necessarily even
 * downloaded. Built for the `.cache` file's shape (a flat JSON array of flat
 * objects, `[{...}, {...}, ...]`); correctly skips braces inside string
 * values (so a `FilePath` containing a literal `{`/`}` can't corrupt the
 * scan) via the same escape-aware string tracking a real JSON tokenizer
 * uses, but doesn't attempt anything beyond that — the `.cache` format has no
 * nested objects, so a general streaming JSON parser is more machinery than
 * this needs. (Nesting is still tracked via `depth`, so even an unexpectedly
 * nested entry parses correctly — `onObject` only ever fires for objects back
 * at depth 0, i.e. the array's own top-level elements.)
 */
/** Escape-aware JSON string tracking, shared by both stream scanners. */
interface JsonStringState {
  inString: boolean;
  escaped: boolean;
}

/** True when `ch` belongs to a string (including its quotes) — the caller
 *  then ignores it, so braces inside string values never count. */
function consumeStringChar(state: JsonStringState, ch: string): boolean {
  if (!state.inString) {
    if (ch !== '"') return false;
    state.inString = true;
    return true;
  }
  if (state.escaped) state.escaped = false;
  else if (ch === "\\") state.escaped = true;
  else if (ch === '"') state.inString = false;
  return true;
}

function createFlatObjectStreamScanner(onObject: (raw: string) => void): { feed: (chunk: string) => void } {
  let buffer = "";
  let pos = 0;
  let objectStart = -1;
  let depth = 0;
  const strings: JsonStringState = { inString: false, escaped: false };

  function closeObject(): void {
    // A '}' with nothing open (a malformed entry's stray brace, e.g.
    // `{"a":}bad}`) must be ignored rather than driving depth negative —
    // otherwise every object genuinely opened afterwards is undercounted
    // by one and never closes, silently dropping the rest of the stream.
    if (depth === 0) return;
    depth--;
    if (depth === 0 && objectStart !== -1) {
      onObject(buffer.slice(objectStart, pos + 1));
      objectStart = -1;
    }
  }

  function feed(chunk: string): void {
    buffer += chunk;
    for (; pos < buffer.length; pos++) {
      const ch = buffer[pos]!;
      if (consumeStringChar(strings, ch)) continue;
      if (ch === "{") {
        if (depth === 0) objectStart = pos;
        depth++;
      } else if (ch === "}") {
        closeObject();
      }
    }
  }

  return { feed };
}

/**
 * Same idea as `createFlatObjectStreamScanner`, but for the MANIFEST's shape
 * instead of the `.cache` file's: either a bare top-level array of entries,
 * or (the documented, actual shape — see this module's header) an object
 * wrapping one array of entries under a key (`{ entries: [...] }` /
 * `{ templates: [...] }`). An entry itself nests further (`placeholders[].
 * bbox`, …), unlike a flat `.cache` object, so a plain brace-depth-0 trigger
 * would instead fire once for the ENTIRE document. This tracks a full
 * bracket/brace stack instead, remembers the stack depth of the first `[` it
 * ever sees (the one array the manifest's fixed shape ever has at the root —
 * an entry's own nested arrays only appear textually AFTER that point, so
 * they can never be mistaken for it), and fires `onObject` only for `{...}`
 * objects that opened as a direct element of THAT array — however deeply
 * that one object nests internally.
 */
function createEntryStreamScanner(onObject: (raw: string) => void): { feed: (chunk: string) => void } {
  let buffer = "";
  let pos = 0;
  const strings: JsonStringState = { inString: false, escaped: false };
  const stack: Array<"{" | "["> = [];
  /** Stack depth at which an element of the array-of-interest sits — set once,
   *  from the first `[` the document contains. */
  let targetDepth: number | null = null;
  let objectStart = -1;
  let objectOpenDepth = -1;

  function openArray(): void {
    targetDepth ??= stack.length + 1; // depth once inside THIS array
    stack.push("[");
  }

  function closeArray(): void {
    // Mismatched (top isn't "[") means a stray/malformed bracket — ignore
    // rather than pop, same reasoning as closeObject's guard: popping the
    // wrong frame would corrupt every depth check for the rest of the
    // stream, not just this one bad entry.
    if (stack[stack.length - 1] === "[") stack.pop();
  }

  function openObject(): void {
    if (stack.length === targetDepth && objectStart === -1) {
      objectStart = pos;
      objectOpenDepth = stack.length;
    }
    stack.push("{");
  }

  function closeObject(): void {
    // A stray/malformed "}" (nothing open, OR the open frame is actually
    // an array's "[") must be ignored rather than popped — a malformed
    // entry like `{"a":}bad}` closes cleanly on its own, but that lone
    // trailing "}" has no "{" to match. Popping it anyway would remove
    // the ENTRIES ARRAY's own frame, so every object opened afterwards is
    // checked against the wrong depth and never recognised again.
    if (stack[stack.length - 1] !== "{") return;
    stack.pop();
    if (objectStart !== -1 && stack.length === objectOpenDepth) {
      onObject(buffer.slice(objectStart, pos + 1));
      objectStart = -1;
    }
  }

  const handlers: Readonly<Record<string, () => void>> = {
    "[": openArray,
    "]": closeArray,
    "{": openObject,
    "}": closeObject,
  };

  function feed(chunk: string): void {
    buffer += chunk;
    for (; pos < buffer.length; pos++) {
      const ch = buffer[pos]!;
      if (consumeStringChar(strings, ch)) continue;
      handlers[ch]?.();
    }
  }

  return { feed };
}

/** Reads a `Response` body as UTF-8 text, chunk by chunk, so a caller can act
 *  on data before the whole response has arrived. Falls back to a single
 *  `res.text()` call on a host with no streamable body (nothing lost — the
 *  caller just gets one big "chunk" instead of several small ones). */
async function streamResponseText(res: Response, onChunk: (text: string) => void): Promise<void> {
  const body = res.body;
  if (!body) {
    onChunk(await res.text());
    return;
  }
  const reader = body.getReader();
  const decoder = new TextDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    onChunk(decoder.decode(value, { stream: true }));
  }
  onChunk(decoder.decode()); // flush any trailing buffered multi-byte sequence
}

/**
 * Same contract as `fetchThumbnailLookup` (best-effort — never rejects), but
 * parses the `.cache` file AS IT DOWNLOADS instead of waiting for the whole
 * response, calling `onEntry` for every real photo the instant its object is
 * complete. Built so a caller who already knows a handful of ids it actually
 * cares about (useTemplatePicker's outline picks) can react to just those the
 * moment they're found, well before a large library's remaining thousand-odd
 * entries have finished arriving — see this module's header for why that
 * matters. Whether it actually arrives early depends on where in the file
 * those entries happen to sit; this doesn't (can't) change the file's own
 * ordering, only how soon this side can act on what's already arrived.
 *
 * The returned map is the same complete lookup `fetchThumbnailLookup` would
 * have produced (every entry, not just the ones `onEntry` was called for) —
 * a caller with no priority ids can pass a no-op `onEntry` and get identical
 * behaviour to the non-streaming function, just read from the wire
 * differently.
 */
async function fetchThumbnailLookupStreaming(
  directorySasUrl: string,
  manifestBlobPath: string,
  signal: AbortSignal | undefined,
  onEntry: (key: string, url: string) => void,
): Promise<ReadonlyMap<string, string>> {
  const cacheFileName = deriveThumbnailCacheFileName(manifestBlobPath);
  if (!cacheFileName) {
    console.info(`[slide-templates] no derivable thumbnail cache for ${manifestBlobPath} — skipping (wireframes only)`);
    return new Map();
  }

  const cacheFileUrl = directorySasFileUrl(directorySasUrl, cacheFileName, (message) =>
    logSlideTemplatesError("slide-templates-thumbnail-cache-url-rebuild", message),
  );
  const startedAt = Date.now();
  console.info(`[slide-templates] streaming thumbnail cache: ${cacheFileUrl}`);
  const lookup = new Map<string, string>();
  try {
    const res = await fetch(cacheFileUrl, {
      method: "GET",
      headers: sasVersionHeaders(cacheFileUrl, logSlideTemplatesError, "slide-templates-sas-version-parse"),
      ...(signal ? { signal } : {}),
    });
    if (!res.ok) throw new Error(`Thumbnail cache download failed (${res.status})`);

    const scanner = createFlatObjectStreamScanner((raw) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        return; // one malformed entry is skipped, not fatal to the rest of the stream
      }
      if (typeof parsed !== "object" || parsed === null) return;
      const entry = parsed as RawThumbnailCacheEntry;
      const filePath = firstString(entry.FilePath);
      const slideNumber = firstNumber(entry.SlideNumber);
      const imageBase64 = firstString(entry.Base64ImageData);
      if (!filePath || slideNumber === null || !imageBase64) return;
      const key = thumbnailLookupKey(filePath, slideNumber);
      if (lookup.has(key)) return; // first entry for a repeated key wins, same as buildThumbnailLookup
      const url = `data:${sniffImageMimeType(imageBase64)};base64,${imageBase64}`;
      lookup.set(key, url);
      onEntry(key, url);
    });

    await streamResponseText(res, (chunk) => scanner.feed(chunk));

    console.info(
      `[slide-templates] thumbnail cache ${cacheFileName} (streamed): ${lookup.size} photo(s) in ${Date.now() - startedAt}ms`,
    );
    return lookup;
  } catch (error) {
    console.warn(`[slide-templates] thumbnail cache ${cacheFileName} failed after ${Date.now() - startedAt}ms:`, error);
    logSlideTemplatesError(
      "slide-templates-thumbnail-cache-fetch",
      getErrorMessage(error, "Thumbnail cache fetch/parse failed"),
    );
    // Whatever streamed in before the failure is still real and still
    // useful — onEntry has already reported it, so the map should agree.
    return lookup;
  }
}

/**
 * Fetches ONLY the `.cache` sibling's real-photo lookup, streaming it in and
 * reacting to entries as they arrive — see `fetchThumbnailLookupStreaming`.
 * Sibling of `fetchSlideTemplateThumbnails`, which is simpler (one shot, no
 * early callback) and is what every caller without a priority list should
 * keep using.
 */
export async function fetchSlideTemplateThumbnailsStreaming(
  sasInfo: SlideTemplatesSasInfo,
  signal: AbortSignal | undefined,
  onEntry: (key: string, url: string) => void,
): Promise<ReadonlyMap<string, string>> {
  return fetchThumbnailLookupStreaming(sasInfo.directorySasUrl, sasInfo.manifestBlobPath, signal, onEntry);
}

async function downloadManifest(sasUrl: string, signal: AbortSignal | undefined): Promise<unknown> {
  const res = await fetch(sasUrl, {
    method: "GET",
    // A user-delegation SAS must echo its own `sv` back as x-ms-version or
    // Azure rejects it with a 403 that reads like a permissions problem —
    // see shared/services/blobImageUpload.ts's SAS_VERSION_HEADER doc. Same
    // requirement its own putBlob has for the upload (PUT) side.
    headers: sasVersionHeaders(sasUrl, logSlideTemplatesError, "slide-templates-sas-version-parse"),
    ...(signal ? { signal } : {}),
  });
  if (!res.ok) {
    const errText = await res.text().catch((error: unknown) => {
      logSlideTemplatesError("slide-templates-manifest-body-read", getErrorMessage(error, "Failed to read manifest error body"));
      return "";
    });
    throw new Error(`${MANIFEST_DOWNLOAD_FAILED_MESSAGE} (${res.status} ${errText})`);
  }
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (error) {
    logSlideTemplatesError("slide-templates-manifest-parse", getErrorMessage(error, "Manifest JSON.parse failed"));
    throw new Error(MANIFEST_NOT_JSON_MESSAGE);
  }
}

/**
 * The SAS token + the manifest's own blob path — everything needed to fetch
 * this template library directly, with a plain unauthenticated `fetch()`. A
 * SAS query string is self-contained auth, so ANY context holding one of
 * these — the pane, or the Office Dialog window, which has no bearer token —
 * can call `fetchSlideTemplatesFromSasInfo` with it directly. Only the
 * initial mint (`requestSlideTemplatesSasInfo`, an authenticated POST) has to
 * happen in the pane. Mirrors presentation-templates/services/
 * presentationTemplatesApi.ts's `DirectorySasInfo` — this feature keeps its
 * own copy rather than sharing the type, same standalone-feature discipline
 * as everything else here.
 */
export interface SlideTemplatesSasInfo {
  readonly directorySasUrl: string;
  readonly manifestBlobPath: string;
}

/** Mints the SAS token — the one step that needs an authenticated
 *  `HttpClient`, so this only ever runs in the pane. */
export async function requestSlideTemplatesSasInfo(
  http: HttpClient,
  options: HttpRequestOptions = {},
  templateType: SlideTemplateLibraryType = SLIDE_TEMPLATES_TEMPLATE_TYPE,
): Promise<SlideTemplatesSasInfo> {
  const { directorySasUrl, manifestBlobPath } = await requestManifestSasAssets(http, options, templateType);
  return { directorySasUrl, manifestBlobPath };
}

/** The manifest's own fetchable URL, rebuilt from its directory SAS. */
function manifestFileUrlFor(directorySasUrl: string, manifestBlobPath: string): string {
  return directorySasFileUrl(directorySasUrl, manifestBlobPath, (message) =>
    logSlideTemplatesError("slide-templates-sas-directory-rebuild", message),
  );
}

/** Resolves an entry's `pptxFile` to a fetchable URL — every source .pptx is
 *  a sibling of the manifest under the same directory SAS. */
function sourceFileUrlResolver(directorySasUrl: string): (pptxFile: string) => string {
  return (pptxFile) =>
    directorySasFileUrl(directorySasUrl, pptxFile, (message) =>
      logSlideTemplatesError("slide-templates-source-url-rebuild", message),
    );
}

/**
 * Fetches + parses ONLY the manifest — the lightweight "AI layout-matching
 * index" (see module header), with no images in it at all. This is the part
 * that has to finish before a card can be shown or swapped at all, and it's
 * fast regardless of catalog size: 1500+ entries of id/category/geometry is a
 * small JSON payload.
 *
 * Every entry's `thumbnailUrl` comes back null (falling back to its wireframe
 * `preview`) until/unless `fetchSlideTemplateThumbnails` + `applyThumbnailLookup`
 * layer real photos on afterward — see those functions' own docs for why that
 * has to be a separate step rather than folded back into this one.
 */
export async function fetchSlideTemplateManifest(
  sasInfo: SlideTemplatesSasInfo,
  signal?: AbortSignal,
): Promise<SlideTemplate[]> {
  const { directorySasUrl, manifestBlobPath } = sasInfo;
  const manifestFileUrl = manifestFileUrlFor(directorySasUrl, manifestBlobPath);
  const manifest = await downloadManifest(manifestFileUrl, signal);
  const resolveSourceFileUrl = sourceFileUrlResolver(directorySasUrl);
  return normaliseManifest(manifest, resolveSourceFileUrl, new Map());
}

/**
 * Same manifest as `fetchSlideTemplateManifest`, normalised the same way, but
 * parsed AS IT DOWNLOADS via `createEntryStreamScanner`, calling `onEntry`
 * with each entry the instant it's syntactically complete — well before a
 * large library's remaining thousand-odd entries have finished arriving.
 * Built for the same reason `fetchSlideTemplateThumbnailsStreaming` was: a
 * caller (useTemplatePicker) that already knows a handful of ids it actually
 * cares about — the outline's own suggestions — so those can get a wireframe
 * (or, once the `.cache` fetch lands too, a real photo) on screen the moment
 * they're found, not gated on the rest of the manifest. Whether that actually
 * happens sooner depends on where in the file those entries happen to sit —
 * same caveat as the thumbnail side, this doesn't (can't) reorder the file.
 *
 * `onEntry` fires for every entry parsed, not just ids the caller cares about
 * (filtering by priority is the caller's job, mirroring the thumbnail
 * function) — and, UNLIKE that one, this does not catch its own errors: the
 * manifest is the whole template list, not a best-effort enhancement, so a
 * caller here gets the exact same failure contract as `fetchSlideTemplateManifest`
 * (rejects; `fetchCatalog`'s `Promise.allSettled` is what turns a per-library
 * failure into a partial-catalog notice rather than losing the others).
 */
export async function fetchSlideTemplateManifestStreaming(
  sasInfo: SlideTemplatesSasInfo,
  signal: AbortSignal | undefined,
  onEntry: (template: SlideTemplate) => void,
): Promise<SlideTemplate[]> {
  const { directorySasUrl, manifestBlobPath } = sasInfo;
  const manifestFileUrl = manifestFileUrlFor(directorySasUrl, manifestBlobPath);
  const resolveSourceFileUrl = sourceFileUrlResolver(directorySasUrl);

  const res = await fetch(manifestFileUrl, {
    method: "GET",
    headers: sasVersionHeaders(manifestFileUrl, logSlideTemplatesError, "slide-templates-sas-version-parse"),
    ...(signal ? { signal } : {}),
  });
  if (!res.ok) {
    const errText = await res.text().catch((error: unknown) => {
      logSlideTemplatesError("slide-templates-manifest-body-read", getErrorMessage(error, "Failed to read manifest error body"));
      return "";
    });
    throw new Error(`${MANIFEST_DOWNLOAD_FAILED_MESSAGE} (${res.status} ${errText})`);
  }

  const templates: SlideTemplate[] = [];
  let index = 0;
  const scanner = createEntryStreamScanner((raw) => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return; // one malformed entry is skipped, not fatal to the rest of the stream
    }
    const template = normaliseManifestEntry(parsed, index++, resolveSourceFileUrl, new Map());
    if (!template) return;
    templates.push(template);
    onEntry(template);
  });

  await streamResponseText(res, (chunk) => scanner.feed(chunk));
  return templates;
}

/**
 * Fetches ONLY the `.cache` sibling's real-photo lookup (see module header) —
 * the part that can genuinely be large (one base64 image per entry, for
 * potentially thousands of entries) and therefore slow to download AND to
 * `JSON.parse`. Deliberately its own call, not folded into the manifest fetch,
 * so a caller can put up a fully working, swappable catalog the instant the
 * manifest arrives and let real thumbnails fill in whenever this finishes —
 * see `applyThumbnailLookup`. Never rejects (mirrors `fetchThumbnailLookup`'s
 * own best-effort contract): a missing/broken cache just yields an empty map.
 */
export async function fetchSlideTemplateThumbnails(
  sasInfo: SlideTemplatesSasInfo,
  signal?: AbortSignal,
): Promise<ReadonlyMap<string, string>> {
  return fetchThumbnailLookup(sasInfo.directorySasUrl, sasInfo.manifestBlobPath, signal);
}

/** The same `{basename(pptxFile)}#{slideIndex}` key `fetchThumbnailLookup`
 *  keys its map by, rebuilt from an already-normalised template rather than
 *  the raw manifest entry — `sourceFileUrl` already carries the original
 *  filename (directorySasFileUrl only ever swaps it onto a different SAS), so
 *  there's no need to keep the raw entry around just for this. */
function templateThumbnailKey(template: SlideTemplate): string | null {
  if (!template.sourceFileUrl || template.slideIndex === null) return null;
  const pathOnly = template.sourceFileUrl.split("?")[0] ?? template.sourceFileUrl;
  return thumbnailLookupKey(basename(pathOnly), template.slideIndex);
}

/**
 * Layers a thumbnail lookup (from `fetchSlideTemplateThumbnails`) onto
 * already-normalised templates, filling in `thumbnailUrl` for any that don't
 * already have one. Pure and non-destructive — templates with no match, or
 * with no `sourceFileUrl`/`slideIndex` to key by, are returned unchanged, so
 * calling this again later (once a slower cache fetch finally resolves) is
 * always safe to just re-apply over the current list.
 */
export function applyThumbnailLookup(
  templates: readonly SlideTemplate[],
  lookup: ReadonlyMap<string, string>,
): SlideTemplate[] {
  if (lookup.size === 0) return [...templates];
  return templates.map((template) => {
    if (template.thumbnailUrl) return template;
    const key = templateThumbnailKey(template);
    const thumbnailUrl = key ? lookup.get(key) : undefined;
    return thumbnailUrl ? { ...template, thumbnailUrl } : template;
  });
}

/**
 * Fetches + parses the slide template manifest AND its real-thumbnail cache
 * together, normalised into a lenient, UI-ready shape. Pure network calls
 * only (`fetch`, no `HttpClient`/bearer token), so this is safe to call from
 * EITHER the pane or the Office Dialog window directly — see
 * `SlideTemplatesSasInfo`'s doc comment.
 *
 * Kept as a single combined call for a caller that's fine waiting for both
 * (a small library, or a context with no separate "ready" state to speed up).
 * A caller that wants the catalog usable the instant the fast manifest
 * arrives — rather than blocked on the `.cache` file, which can be large
 * enough to take minutes on a big library — should call
 * `fetchSlideTemplateManifest` and `fetchSlideTemplateThumbnails` separately
 * instead and merge with `applyThumbnailLookup` once the second one resolves.
 */
export async function fetchSlideTemplatesFromSasInfo(
  sasInfo: SlideTemplatesSasInfo,
  signal?: AbortSignal,
): Promise<SlideTemplate[]> {
  // Independent fetches — the manifest download must succeed (it's the whole
  // template list), but the thumbnail cache is best-effort on its own and
  // never blocks or fails the manifest fetch; running them together instead
  // of sequentially halves the wait for the common case where both succeed.
  const [templates, thumbnailLookup] = await Promise.all([
    fetchSlideTemplateManifest(sasInfo, signal),
    fetchSlideTemplateThumbnails(sasInfo, signal),
  ]);
  return applyThumbnailLookup(templates, thumbnailLookup);
}

/**
 * Mints a directory SAS (needs an authenticated `http`, so this is the
 * pane-only entry point) and fetches the library through it. The Office
 * Dialog window instead mints via a round-trip to the pane
 * (`requestSlideTemplatesSasInfo` is what the pane calls) and then calls
 * `fetchSlideTemplatesFromSasInfo` itself directly — see
 * asset-library/hooks/useDialogSlideTemplates.ts.
 */
export async function fetchSlideTemplates(http: HttpClient, options: HttpRequestOptions = {}): Promise<SlideTemplate[]> {
  const sasInfo = await requestSlideTemplatesSasInfo(http, options);
  return fetchSlideTemplatesFromSasInfo(sasInfo, options.signal);
}
